import { scry, scryImageUrl } from "@/lib/scryClient";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  collection,
  query,
  getDocs,
  where,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { Button } from "@/components/ui/button";
import Share from "@/assets/icons/atlas/share.svg?react";
import CardDetailsModal from "@/components/CardDetailsModal";
import { ensureDmThreadFor } from "@/lib/dm";
import { toast } from "@/components/ui/use-toast";
import { openOfferComposer } from "@/components/offers/OfferComposerModal";
import { FoilStyles } from "@/components/cards/CardTile";
import { sendOfferV2 } from "@/data/offersV2";
import FollowButtonV3 from "@/components/follow/FollowButtonV3"; // NEW
import cx from "clsx"; // FIX: ensure cx is defined (was referenced but not imported)
import { createPortal } from "react-dom"; // NEW
import { PriceOverlay } from "@/components/PriceOverlay"; // ADD

/* ---------------- Types ---------------- */
export type ListingProfile = {
  uid: string;
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  rank_title?: string | null;
  follow_privacy?: string | null;
  preferences?: {
    available_for_trading?: boolean;
    available_for_play?: boolean;
  };
  account_type?: string | null;
  is_lgs?: boolean;
  [k: string]: any;
};

export type ListingData = {
  id: string;
  name: string;
  image: string | null;
  status: "sale" | "trade" | "keep";
  price: number | null;
  foil?: boolean;
  set?: string | null;
  number?: string | number | null;
  scryfall_id?: string | null;
  oracle_id?: string | null;
};

// Add helper if publicProfilePath was removed / not imported
// (Remove this if you already import a real publicProfilePath elsewhere)
function publicProfilePath(p: ListingProfile) {
  if (p.username) return `/u/${p.username}`;
  return `/u/id/${p.uid}`;
}
function scryImg(id?: string | null, version: "small" | "normal" = "normal") {
  return id
    ? `https://api.scryfall.com/cards/${encodeURIComponent(String(id))}?format=image&version=${version}`
    : null;
}
/* ---------- Component ---------- */
type Props = {
  profile: ListingProfile;
  listing: ListingData;
  distanceMiles?: number | string | null;
  onOpenZoom?: (d: any) => void;
  onOpenProfile?: (uid: string) => void;
  className?: string;
  focusMode?: boolean; // NEW
};

type MiniCard = ListingData & {
  thumb?: string | null;
  image_small?: string | null;
  condition?: string | null;
  qty?: number | null;
};

export default function ListingProfileCard({
  profile,
  listing,
  distanceMiles,
  onOpenZoom,
  onOpenProfile,
  className = "",
  focusMode = false,
}: Props) {
  const [fullCardOpen, setFullCardOpen] = useState(false);
  // NEW: image lightbox state (mirrors NewMiniProfileCard pattern)
  const [imageLightboxOpen, setImageLightboxOpen] = useState(false);
  const articleRef = useRef<HTMLElement | null>(null);

  function handleCardClick(e: React.MouseEvent) {
    if ((e.target as Element).closest('[data-no-modal]')) return;
    if ((e.target as Element).closest('[data-allow-modal]')) {
      // image button now handled separately (opens lightbox); leave safeguard
      if (!focusMode) setFullCardOpen(true);
      return;
    }
    if ((e.target as Element).closest('a, button, input, textarea, select, label')) return;
    if (!focusMode) setFullCardOpen(true); // only open if not already focus mode
  }

  // Defensive guard
  if (!profile || !listing) return null;

  // NEW: extended profile state (mirrors NewMiniProfileCard approach)
  const [profileExtra, setProfileExtra] = useState<any>(
    (profile as any)?.extra ||
      (profile as any)?.profileExtra ||
      (profile as any)?.details ||
      {}
  );
  const [profileExtraLoading, setProfileExtraLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProfileExtraLoading(true);
        const refs = [doc(db, "profiles", profile.uid), doc(db, "users", profile.uid)];
        for (const r of refs) {
          const snap = await getDoc(r).catch(() => null);
          if (snap?.exists()) {
            if (!cancelled)
              setProfileExtra((prev: any) => ({ ...prev, ...snap.data() }));
            // do not break; allow merge of both docs if they differ
          }
        }
      } finally {
        if (!cancelled) setProfileExtraLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile.uid]);

  const nav = useNavigate();
  const me = auth.currentUser?.uid || null;
  const isOwn = me === profile.uid;

  // REMOVE duplicated:
  // const [profileExtra, setProfileExtra] = useState<any>( ... );

  // Derived username (placed early so callbacks can use it)
  const usernameLabel =
    profileExtra?.display_name ||
    profile.display_name ||
    profileExtra?.username ||
    profile.username ||
    (profileExtra?.rank_title || profile.rank_title
      ? `${profileExtra?.rank_title || profile.rank_title}`
      : "") ||
    profile.uid.slice(0, 6);

  const usernameDisplay = usernameLabel.startsWith("@")
    ? usernameLabel
    : `@${usernameLabel}`;

  /* ---------- Availability / prefs ---------- */
  const tradeAvail = profile.preferences?.available_for_trading !== false;
  const playAvail = profile.preferences?.available_for_play !== false;

  /* ---------- LGS heuristic ---------- */
  const isLgs = useMemo(
    () =>
      !!(
        (profileExtra?.rank_title || profile.rank_title || "")
          .toLowerCase()
          .includes("lgs") ||
        profile.account_type === "lgs" ||
        profile.is_lgs ||
        profileExtra?.account_type === "lgs" ||
        profileExtra?.is_lgs
      ),
    [
      profile.rank_title,
      profile.account_type,
      profile.is_lgs,
      profileExtra?.rank_title,
      profileExtra?.account_type,
      profileExtra?.is_lgs,
    ]
  );

  /* ---------- Actions ---------- */
  const handleProfileClick = () => {
    onOpenProfile
      ? onOpenProfile(profile.uid)
      : nav(publicProfilePath(profile));
  };

  // Rich zoom modal state (must be declared before use)
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomData, setZoomData] = useState<ListingData | null>(null);
  const zoomModalRef = useRef<HTMLDivElement | null>(null);

  const handleZoom = () => {
    onOpenZoom?.({
      ...listing,
      ownerUid: profile.uid,
    });
    setZoomData(listing);
    setZoomOpen(true);
  };

  // Optional: close zoom on ESC
  useEffect(() => {
    if (!zoomOpen) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [zoomOpen]);

  // Close zoom modal on outside click / touch (more reliable than backdrop target check)
  useEffect(() => {
    if (!zoomOpen) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (!zoomModalRef.current) return;
      if (zoomModalRef.current.contains(e.target as Node)) return;
      setZoomOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [zoomOpen]);

  const [cardDetailsOpen, setCardDetailsOpen] = useState(false);
  const [cardDetailsData, setCardDetailsData] = useState<any | null>(null);
  // Quick message modal state (copied flow from NewMiniProfileCard)
  const [msgOpenModal, setMsgOpenModal] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [msgFile, setMsgFile] = useState<File | null>(null);
  const [msgSending, setMsgSending] = useState(false);

  async function sendQuickMessage() {
    if (!auth.currentUser) return;
    const me = auth.currentUser.uid;
    const them = profile.uid;
    if (me === them) return;
    const text = (msgBody || "").trim();
    if (!text && !msgFile) {
      toast({ description: "Type a message or attach a file." });
      setTimeout(() => toast({ description: null }), 1500);
      return;
    }
    setMsgSending(true);
    try {
      // 1) Resolve a canonical thread id (prefer ensureDmThreadFor which creates/reuses canonical shell)
      let threadId: string | null = null;
      try {
        const got = await ensureDmThreadFor(me, them);
        if (typeof got === "string" && got) {
          threadId = got;
        }
      } catch (e) {
        console.warn("ensureDmThreadFor failed, falling back to local resolver", e);
      }

      // 2) Fallback: create canonical dm2:<a>_<b> shell (idempotent via setDoc)
      if (!threadId) {
        const dm2 = `dm2:${[me, them].sort().join("_")}`;
        try {
          await setDoc(
            doc(db, "threads_v2", dm2),
            {
              participants: [me, them].sort(),
              visibleFor: [me, them].sort(),
              lastMessageAt: serverTimestamp(),
              messageCount: 0,
              unreadCounts: { [me]: 0, [them]: 0 },
              tab: "inbox",
            },
            { merge: true }
          );
          threadId = dm2;
        } catch (e) {
          // last resort: create an auto-id thread (should rarely happen)
          const threadDoc = await addDoc(collection(db, "threads_v2"), {
            participants: [me, them],
            visibleFor: [me, them],
            updatedAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
            lastMessage: null,
            lastSenderId: null,
            messageCount: 0,
          });
          threadId = threadDoc.id;
        }
      }

      // 3) Attachment upload (best-effort)
      let attachmentUrl: string | null = null;
      if (msgFile) {
        try {
          const { getStorage, ref, uploadBytes, getDownloadURL } = await import(
            "firebase/storage"
          );
          const storage = getStorage();
          const key = `messages/${me}/${Date.now()}-${msgFile.name}`;
          const fileRef = ref(storage, key);
          await uploadBytes(fileRef, msgFile);
          attachmentUrl = await getDownloadURL(fileRef);
        } catch (e) {
          console.warn("Attachment upload failed, sending without attachment.", e);
        }
      }

      // 4) Post message
      const payload: any = {
        threadId,
        senderId: me,
        recipientId: them,
        participants: [me, them],
        body: text || (attachmentUrl ? "(attachment)" : ""),
        createdAt: serverTimestamp(),
        isRead: false,
      };
      if (attachmentUrl) payload.attachmentUrls = [attachmentUrl];
      await addDoc(collection(db, "messages_v2"), payload);

      // 5) Update thread meta (best-effort)
      try {
        await updateDoc(doc(db, "threads_v2", threadId), {
          updatedAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessage: (text || "ðŸ“Ž Attachment").slice(0, 240),
          lastSenderId: me,
        });
      } catch (e) {
        console.warn("Thread metadata update failed (non-fatal):", e);
      }

      // 6) Create notification for recipient (best-effort)
      try {
        await addDoc(collection(db, "users", them, "notifications"), {
          toUid: them,
          fromUid: me,
          type: "message",
          text: text || "Attachment",
          conversationId: threadId,
          createdAt: serverTimestamp(),
          read: false,
          isRead: false,
        });
      } catch (e) {
        console.warn("Notification create failed (non-fatal):", e);
      }

      setMsgOpenModal(false);
      setMsgBody("");
      setMsgFile(null);
      toast({ description: `Message sent to ${usernameDisplay}` });
      setTimeout(() => toast({ description: null }), 1600);
    } catch (e) {
      console.error(e);
      toast({ description: "Could not send message. Try again." });
      setTimeout(() => toast({ description: null }), 1600);
    } finally {
      setMsgSending(false);
    }
  }

  const openOffer = useCallback(() => {
    if (!auth.currentUser) {
      toast({ description: "Sign in to send an offer." });
      setTimeout(() => toast({ description: null }), 1600);
      return;
    }
    if (auth.currentUser.uid === profile.uid) return;
    if (listing.status !== "sale" && listing.status !== "trade") return;
    setOfferMessage("");
    setOfferPrice("");
    // NEW: ensure offerCard is set so modal renders
    setOfferCard({
      id: listing.id,
      name: listing.name,
      image: listing.image,
      status: listing.status,
      price: listing.price,
      foil: listing.foil,
      set: listing.set,
      number: listing.number,
      scryfall_id: listing.scryfall_id || listing.oracle_id || undefined,
      oracle_id: listing.oracle_id || undefined,
    } as any);
    setOfferOpen(true);
  }, [profile.uid, listing.status, listing.id, listing.name, listing.image, listing.price, listing.foil, listing.set, listing.number, listing.scryfall_id, listing.oracle_id]);

  const [zoomCard, setZoomCard] = useState<MiniCard | null>(null);

  // Foil parallax overlay refs + handlers (reuse/approximate MyCollection foil)
  const foilRef = React.useRef<HTMLDivElement | null>(null);
  const handleFoilMove = React.useCallback((e: React.MouseEvent) => {
    if (!foilRef.current) return;
    const host = e.currentTarget as HTMLElement;
    const rect = host.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const tx = (x - 0.5) * 18; // horizontal parallax
    const ty = (y - 0.5) * 10; // vertical parallax
    foilRef.current.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(1.05)`;
    foilRef.current.style.opacity = "1";
  }, []);
  const resetFoil = React.useCallback(() => {
    if (!foilRef.current) return;
    foilRef.current.style.transform = "";
    foilRef.current.style.opacity = "";
  }, []);

  // Unified offer flow state (NEW)
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerCard, setOfferCard] = useState<MiniCard | null>(null);
  const [offerMessage, setOfferMessage] = useState("");
  const [offerPrice, setOfferPrice] = useState<string>("");
  const [submittingOffer, setSubmittingOffer] = useState(false);

  const submitOffer = useCallback(async () => {
    if (!auth.currentUser || !offerCard || auth.currentUser.uid === profile.uid) return;
    if (offerCard.status !== "sale" && offerCard.status !== "trade") return;
    setSubmittingOffer(true);
    try {
      const cardRefPath =
        offerCard.id && profile.uid
          ? `users/${profile.uid}/cards/${offerCard.id}`
          : null;

      await sendOfferV2({
        toUid: profile.uid,
        offerType: offerCard.status === "sale" ? "purchase" : "trade",
        offeredPrice:
          offerCard.status === "sale" && offerPrice
            ? Number(offerPrice)
            : null,
        message: (offerMessage || "").trim() || null,
        preview: (offerMessage || "").trim().slice(0, 160) || null,
        cardId: offerCard.id,
        cardOwnerUid: profile.uid,
        cardRefPath,
        scryfall_id: offerCard.scryfall_id || offerCard.oracle_id || null,
        cardName: offerCard.name || null,
        cardImage: offerCard.image || null,
        cardThumb: offerCard.thumb || (offerCard as any).image_small || offerCard.image || null, // FIX: replace 'card' with 'offerCard'
        cardSet: (offerCard as any).set || null,
        cardNumber: (offerCard as any).number || null,
        cardFoil: !!offerCard.foil,
        cardCondition: (offerCard as any).condition || null,
        cardQty: (offerCard as any).qty ?? 1,
        cardStatus: offerCard.status,
        listingPrice:
          offerCard.status === "sale"
            ? (offerCard as any).price ?? null
            : null,
        currency: "USD",
      });
      setOfferOpen(false);
      toast({ description: `Offer sent to ${usernameDisplay}` });
      setTimeout(() => toast({ description: null }), 2000);
    } catch (e) {
      console.error(e);
      toast({ description: "Could not send offer. Please try again." });
      setTimeout(() => toast({ description: null }), 2200);
    } finally {
      setSubmittingOffer(false);
    }
  }, [offerCard, offerPrice, offerMessage, profile.uid, usernameDisplay]);

  const handleOffer = () => {
    if (!auth.currentUser) {
      toast({ description: "Sign in to send an offer." });
      setTimeout(() => toast({ description: null }), 1600);
      return;
    }
    if (auth.currentUser.uid === profile.uid) return;
    if (listing.status !== "sale" && listing.status !== "trade") return;

    const cardRefPath = `users/${profile.uid}/cards/${listing.id}`;

    openOfferComposer({
      toUid: profile.uid,
      toUsername:
        profile.username ||
        profile.display_name ||
        profile.uid.slice(0, 6),
      card: {
        cardId: listing.id,
        cardOwnerUid: profile.uid,      // NEW
        cardRefPath,                    // NEW
        name: listing.name,
        cardName: listing.name,         // alias
        image: scryImg(listing.scryfall_id, "normal"),
    cardImage: scryImg(listing.scryfall_id, "normal"),
        set: listing.set || null,
        number: listing.number ?? null,
        cardNumber: listing.number ?? null,
        price: listing.status === "sale" ? listing.price ?? null : null,
        listingPrice: listing.status === "sale" ? listing.price ?? null : null,
        status: listing.status,
        cardStatus: listing.status,
        foil: listing.foil,
        cardFoil: listing.foil,
        condition: (listing as any).condition || null,
        cardCondition: (listing as any).condition || null,
        scryfall_id: listing.scryfall_id || listing.oracle_id || null,
        oracle_id: listing.oracle_id || null,
        cardQty: (listing as any).qty ?? 1,
        currency: "USD",
      },
    });
  };

  const handleDM = () => {
    const meUid = auth.currentUser?.uid;
    if (!meUid || meUid === profile.uid) return;
    ensureDmThreadFor(meUid, profile.uid)
      .then(() => nav(`/inbox?to=${profile.uid}`))
      .catch(() => nav(`/inbox?to=${profile.uid}`));
  };

  const shareProfile = async () => {
    const url = window.location.origin + publicProfilePath(profile);
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({
          title: profile.username || profile.display_name || "Profile",
          url,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
    } catch {}
  };

  const avatarSrc =
    profileExtra?.avatar_url ||
    profile.avatar_url ||
    (isLgs ? "/images/lgs_default.png" : "/images/avatar-default.png");

  /* ---------- Display ---------- */
  // Accept number or string, normalize to a finite number
  const rawDistance = distanceMiles as any;
  let normalizedDistance =
    typeof rawDistance === "number"
      ? rawDistance
      : typeof rawDistance === "string"
      ? parseFloat(rawDistance)
      : NaN;

  // Fallback: derive distance if not provided but we have coordinates
  if (!Number.isFinite(normalizedDistance)) {
    const fallback = deriveDistanceMiles(
      profileExtra,
      profile,
      // Attempt to read current user coords from localStorage (adjust keys to your app)
      localStorage.getItem("user_lat"),
      localStorage.getItem("user_lng")
    );
    if (fallback != null) normalizedDistance = fallback;
  }

  const hasDistance = Number.isFinite(normalizedDistance);
  const distanceMilesDisplay = hasDistance
    ? normalizedDistance.toFixed(1)
    : null;

  if (process.env.NODE_ENV !== "production") {
    // Quick dev trace (remove if noisy)
    console.debug(
      "[ListingProfileCard] distanceMiles prop:",
      distanceMiles,
      "resolved:",
      distanceMilesDisplay
    );
  }

  /* ---------- Styling ---------- */
  const topBarClasses = isLgs
    ? "from-[#00aaff]/70 via-[#0088ff]/55 to-[#002b66]/40"
    : // neutral black/gray gradient
      "from-black/85 via-gray-800/65 to-gray-700/40";
  const bottomBarClasses = isLgs
    ? "from-[#007fe0]/70 via-[#006ad1]/55 to-[#004b99]/45"
    : // neutral darker gradient for footer
      "from-gray-900/90 via-gray-800/65 to-gray-700/45";

  /* ---------- Image ---------- */
  // CARD IMAGE (flush stage)
const cardImageSrc = scryImg(listing.scryfall_id, "normal") || "/images/sg-card-back.png";



  return (
    <article
      ref={articleRef}
      onClick={handleCardClick}
      data-focus-mode={focusMode ? "true" : "false"} // NEW
      className={cx(
        "profile-card-root flex flex-col w-full overflow-hidden rounded-xl bg-black/55 backdrop-blur-sm relative",
        className
      )}
      style={focusMode ? { height: "100%", maxHeight: "100%", minHeight: "100%" } : undefined} // NEW
    >
      {/* Top bar */}
      <div
        className={cx(
          "px-3 pt-3 pb-0 sm:px-4 sm:pt-4 sm:pb-3",
          "border-b border-white/10",
          "bg-gradient-to-r",
          topBarClasses,
          "backdrop-blur-md shrink-0",
          "rounded-t-xl"
        )}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={publicProfilePath(profile)}
              className="group inline-flex items-center gap-2"
              title={`Open ${usernameLabel}'s profile`}
              onClick={(e) => {
  if (onOpenProfile) {
    e.preventDefault();
   onOpenProfile(profile.uid); // FIX: pass UID (matches prop type)
  }
}}
            >
              <img
                src={avatarSrc}
                alt={usernameLabel}
                className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20 bg-black/30"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = isLgs
                    ? "/images/lgs_default.png"
                    : "/images/avatar-default.png";
                }}
              />
              <span
                className="text-base font-medium text-white group-hover:underline flex items-center gap-1 min-w-0"
                title={
                  distanceMilesDisplay
                    ? `${usernameDisplay} - ${distanceMilesDisplay} miles away`
                    : usernameDisplay
                }
              >
                <span className="truncate">{usernameDisplay}</span>
                {distanceMilesDisplay && (
                  <span className="flex-shrink-0 text-[12px] text-white/60">
                    - {distanceMilesDisplay} miles
                  </span>
                )}
              </span>
            </Link>
          </div>
          {/* ...existing (right side actions, badges, etc.) ... */}
        </div>
      </div>

      {/* CARD IMAGE (flush stage) */}
      <div className="card-image-stage relative aspect-[63/88] w-full bg-black/40 m-0">
        <button
          type="button"
          data-allow-modal
          onClick={(e) => {
            e.stopPropagation();
            setImageLightboxOpen(true);
          }}
          className="group absolute inset-0 focus:outline-none"
        >
          <img
            src={cardImageSrc}
            alt={listing.name}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.035]"
            draggable={false}
          />
          {(listing.status === "sale" || listing.status === "trade") && (
            <div className="absolute left-1 top-1 rounded bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white/85 backdrop-blur">
              {listing.status === "sale"
                ? listing.price != null
                  ? `$${listing.price.toFixed(2)}`
                  : "For Sale"
                : "For Trade"}
            </div>
          )}
          {listing.foil && (
            <>
              <div
                className="absolute inset-0 foil-wrap"
                onMouseMove={handleFoilMove}
                onMouseLeave={resetFoil}
                aria-hidden="true"
              >
                <div ref={foilRef} className="foil-layer" />
              </div>
              <FoilStyles />
            </>
          )}
        </button>
        {listing.scryfall_id && (
  <div className="absolute left-1 bottom-1 z-10 pointer-events-none">
    <PriceOverlay
      scryfallId={listing.scryfall_id}
      showSpellgatherAvg
      textScale={0.65}
      flushBackground
      compact
    />
  </div>
)}
      </div>

      {/* FOOTER META */}
      <div
        className={cx(
          "shrink-0 flex items-center justify-between px-3 py-2 text-white/90 bg-gradient-to-r",
          bottomBarClasses,
          "border-t border-white/10"
        )}
      >
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {listing.name || "”"}
          </div>
          <div className="truncate text-xs text-white/70">
            {(listing.set || "").toUpperCase()}{" "}
            {listing.number ? `#${listing.number}` : ""}{" "}
            {listing.foil ? "Foil " : ""}{" "}
            {listing.status === "sale"
              ? "For Sale"
              : listing.status === "trade"
              ? "For Trade"
              : ""}{" "}
            {typeof listing.price === "number"
              ? `$${listing.price.toFixed(2)}`
              : ""}
          </div>
        </div>
      </div>

      {/* ACTION BAR */}
      <div
        data-no-modal
        className={cx(
          "action-bar z-20 shrink-0 flex flex-wrap items-center gap-3 px-4 pb-4 pt-4 bg-gradient-to-r",
          bottomBarClasses
        )}
      >
        {/* NEW FollowButtonV3 replaces legacy follow logic */}
        {!isOwn && (
          <FollowButtonV3
            targetUid={profile.uid}
            targetUsername={
              profile.username ||
              profile.display_name ||
              profile.uid.slice(0, 6)
            }
            className="h-12"
          />
        )}

        {me !== profile.uid && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (!auth.currentUser) {
                toast({ description: "Sign in to send a message." });
                setTimeout(() => toast({ description: null }), 1500);
                return;
              }
              if (auth.currentUser.uid === profile.uid) return;
              setMsgBody("");
              setMsgFile(null);
              setMsgOpenModal(true);
            }}
            className="follow-img-btn h-10 w-12 flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300/50"
            aria-label="Message"
            title="Message"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-8 w-8 text-cyan-200"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 5h16v10H7.5L4 18.5V5z" />
              <path d="M9 10h6M9 7h8" />
            </svg>
          </button>
        )}

        <button
          onClick={shareProfile}
          className="follow-img-btn h-10 w-12 flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300/50"
          aria-label="Share Profile"
          title="Share"
        >
          <Share className="h-8 w-8 text-amber-300" />
        </button>

        {(listing.status === "sale" || listing.status === "trade") &&
          me !== profile.uid && (
            <Button
              className="flex-1 min-w-[120px] justify-center h-10 text-sm bg-amber-400 text-black hover:bg-amber-300 focus:ring-2 focus:ring-amber-300"
              onClick={handleOffer}
              disabled={!auth.currentUser || auth.currentUser.uid === profile.uid}
              title={
                !auth.currentUser
                  ? "Sign in to make an offer"
                  : auth.currentUser?.uid === profile.uid
                  ? "Cannot offer on your own card"
                  : listing.status === "sale"
                  ? "Make an offer"
                  : "Propose a trade"
              }
            >
              {listing.status === "sale" ? "Offer" : "Trade"}
            </Button>
          )}
      {/* CLOSE action bar container */}
      </div>

      {/* CardDetailsModal remains for viewing card details */}
      <CardDetailsModal
        open={cardDetailsOpen}
        onClose={() => setCardDetailsOpen(false)}
        item={cardDetailsData}   // use 'item' prop for unified path
      />

      {/* RICH MOBILE-FORWARD ZOOM MODAL (UPDATED: centered & click-away close) */}
      {zoomOpen && zoomData && (
        <div
          className="fixed inset-0 z-[2147483600] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={zoomData.name}
        >
          <div
            ref={zoomModalRef}
            className="relative w-[min(92vw,420px)] max-h-[90vh] rounded-xl border border-white/10 bg-black/65 backdrop-blur-md shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={() => setZoomOpen(false)}
              className="absolute top-2 right-2 h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm flex items-center justify-center"
              aria-label="Close"
            >
              âœ•
            </button>

            {/* Card image */}
            <div className="relative aspect-[63/88] w-full bg-black/30 border-b border-white/10">
              <img
  src={scryImg(zoomData.scryfall_id, "normal") || cardImageSrc}
                alt={zoomData.name}
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
              {zoomData.foil && (
                <div className="absolute inset-0 foil-wrap pointer-events-none">
                  <div className="foil-layer" />
                </div>
              )}
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-4 pt-4 pb-4 space-y-5 text-sm text-white/85">
              <div className="text-center">
                <div className="text-base font-semibold truncate">{zoomData.name}</div>
                <div className="text-xs text-white/60 mt-1 truncate">
                  {(zoomData.set || "").toUpperCase()}
                  {zoomData.number ? ` #${zoomData.number}` : ""}
                  {zoomData.foil ? " Foil" : ""}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-white/40 mb-1">
                  Owner
                </div>
                <button
                  onClick={() => {
                    setZoomOpen(false);
                    handleProfileClick();
                  }}
                  className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200"
                >
                  <img
                    src={avatarSrc}
                    className="h-6 w-6 rounded-full object-cover ring-1 ring-white/20"
                  />
                  <span className="truncate max-w-[160px] flex items-center gap-1">
                    {usernameDisplay}
                    {distanceMilesDisplay && (
                      <span className="text-[10px] text-white/50">
                        Â· {distanceMilesDisplay} mi
                      </span>
                    )}
                  </span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <LabelVal label="Set" value={(zoomData.set || "").toUpperCase() || "”"} />
                <LabelVal label="Number" value={zoomData.number || "”"} />
                <LabelVal label="Status" value={zoomData.status || "”"} />
                <LabelVal label="Foil" value={zoomData.foil ? "Yes" : "No"} />
                <LabelVal label="Price" value={typeof zoomData.price === "number" ? `$${zoomData.price.toFixed(2)}` : "”"} />
                <LabelVal label="Condition" value={(listing as any).condition || "”"} />
              </div>

              {(listing as any).notes && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-white/40 mb-1">
                    Seller Notes
                  </div>
                  <div className="rounded bg-white/5 p-3 text-white/80 whitespace-pre-wrap text-xs">
                    {(listing as any).notes}
                  </div>
                </div>
              )}

              {zoomData.scryfall_id && (
                <div className="flex flex-wrap gap-4 text-xs">
                  <a
                    href={`https://scryfall.com/card/${zoomData.set || ""}/${zoomData.number || ""}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-cyan-300 hover:text-cyan-200 underline"
                  >
                    View on Scryfall
                  </a>
                  <button
                    onClick={() => copyImageUrl(zoomData)}
                    className="text-white/70 hover:text-white"
                  >
                    Copy Image URL
                  </button>
                  <button
                    onClick={() => saveCardImage(zoomData)}
                    className="text-white/70 hover:text-white"
                  >
                    Save Image
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-4 pb-4">
              <div className="flex gap-3">
                {auth.currentUser?.uid !== profile.uid && (
                  <>
                    <button
                      onClick={() => {
                        setZoomOpen(false);
                        handleOffer();
                      }}
                      disabled={
                        !auth.currentUser ||
                        auth.currentUser.uid === profile.uid ||
                        (zoomData.status !== "sale" && zoomData.status !== "trade")
                      }
                      className="flex-1 h-11 rounded-md bg-amber-400 text-black font-medium text-sm hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {zoomData.status === "sale"
                        ? "Make Offer"
                        : zoomData.status === "trade"
                        ? "Propose Trade"
                        : "Unavailable"}
                    </button>
                    <button
                      onClick={() => {
                        if (!auth.currentUser) {
                          toast({ description: "Sign in to message." });
                          return;
                        }
                        setMsgBody("");
                        setMsgFile(null);
                        setMsgOpenModal(true);
                        setZoomOpen(false);
                      }}
                      className="h-11 rounded-md px-4 bg-white/10 text-white text-sm font-medium hover:bg-white/20"
                    >
                      Message
                    </button>
                  </>
                )}
                <button
                  onClick={() => saveCardImage(zoomData)}
                  className="h-11 rounded-md px-3 bg-white/10 text-white text-sm hover:bg-white/20"
                  title="Download image"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE LIGHTBOX (mirrors NewMiniProfileCard approach) */}
      {imageLightboxOpen &&
        createPortal(
          <ListingImageLightbox
            listing={listing}
            onClose={() => setImageLightboxOpen(false)}
            onOffer={() => {
              if (!auth.currentUser) {
                toast({ description: "Sign in to make an offer." });
                setTimeout(() => toast({ description: null }), 1500);
                return;
              }
              handleOffer();
            }}
            onMessage={() => {
              if (!auth.currentUser) {
                toast({ description: "Sign in to send a message." });
                setTimeout(() => toast({ description: null }), 1500);
                return;
              }
              if (auth.currentUser.uid === profile.uid) return;
              setMsgBody("");
              setMsgFile(null);
              setMsgOpenModal(true);
              setImageLightboxOpen(false);
            }}
            onShare={() => {
              void shareProfile();
            }}
          />,
          document.body
        )
      }

      {/* REPLACED: Full-card modal stays (non-image click) */}
      {fullCardOpen && !focusMode &&
        createPortal(
          <FocusedProfileModal
            onClose={() => setFullCardOpen(false)}
            ariaLabel={`Profile ${usernameDisplay}`}
          >
            <ListingProfileCard
              profile={profile}
              listing={listing}
              distanceMiles={distanceMiles}
              onOpenZoom={onOpenZoom}
              onOpenProfile={onOpenProfile}
              className={className}
              focusMode
            />
          </FocusedProfileModal>,
          document.body
        )
      }

      {/* QUICK MESSAGE MODAL (restored to mirror NewMiniProfileCard) */}
      {msgOpenModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[3600] bg-black/70 backdrop-blur-sm grid place-items-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-message-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setMsgOpenModal(false);
            }}
          >
            <div
              className="rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl p-4 w-[min(92vw,560px)] max-h-[84vh] overflow-auto"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-700 bg-white/5 grid place-items-center">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={usernameDisplay}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[11px] text-white/70 font-bold">
                        {(usernameDisplay || "").replace(/^@/, "").slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3
                      id="quick-message-title"
                      className="text-base font-semibold truncate"
                    >
                      Message {usernameDisplay}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setMsgOpenModal(false)}
                  className="text-slate-300 hover:text-white text-sm px-2 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="grid gap-2 mb-3">
                <label className="text-sm">Message</label>
                <textarea
                  rows={5}
                  className="border rounded px-3 py-2 text-black text-sm"
                  placeholder="Write a quick message..."
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                />
              </div>

              <div className="grid gap-2 mb-2">
                <label className="text-sm">Attachment (optional)</label>
                <input
                  type="file"
                  accept="image/*,text/plain,application/pdf"
                  onChange={(e) => setMsgFile(e.target.files?.[0] ?? null)}
                />
                {msgFile && (
                  <div
                    className="text-xs text-slate-300 truncate"
                    title={msgFile.name}
                  >
                    📎 {msgFile.name}
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setMsgOpenModal(false)}
                  disabled={msgSending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => void sendQuickMessage()}
                  disabled={msgSending}
                >
                  {msgSending ? "Sending…" : "Send"}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      <style>{`
        /* existing styles remain below */
        /* Focus image flush already present; ensure containment */
        .listing-image-lightbox-actions button:focus-visible {
          outline:2px solid rgba(56,189,248,.9);
          outline-offset:2px;
        }

        /* FOCUSED MODE: make whole card fill modal height and let image stage flex */
        .profile-card-root[data-focus-mode="true"] {
          height:100%;
          display:flex;
          flex-direction:column;
        }
        .profile-card-root[data-focus-mode="true"] > .card-image-stage {
          flex:1 1 auto;
          display:flex;
          position:relative;
          background:transparent;
          aspect-ratio:auto !important;
          min-height:0;
          max-height:100%;
          padding:0;
        }
        .profile-card-root[data-focus-mode="true"] > .card-image-stage > button {
          position:static !important;
          inset:auto !important;
          flex:1 1 auto;
          display:flex;
          align-items:center;
          justify-content:center;
          width:100%;
          height:100%;
          padding:0;
        }
        .profile-card-root[data-focus-mode="true"] > .card-image-stage > button > img {
          position:static !important;
          width:100% !important;
          height:100% !important;
          max-width:100%;
          max-height:100%;
          object-fit:contain !important;
          transform:none !important;
        }
        /* Keep hover scale only in non-focused mode */
        .profile-card-root[data-focus-mode="true"] .card-image-stage > button:hover img {
          transform:none;
        }
      `}</style>
    </article>
  );
  
  function LabelVal({ label, value }: { label: string; value: React.ReactNode }) {
    return (
      <div className="space-y-0.5">
        <div className="text-[10px] uppercase tracking-wide text-white/40 font-medium">
          {label}
        </div>
        <div className="text-xs text-white/85 truncate">{value}</div>
      </div>
    );
  }

  function copyImageUrl(card: ListingData) {
  const url = scryImg(card.scryfall_id, "normal");
  if (!url) return;
  navigator.clipboard?.writeText(url)
    .then(() => toast({ description: "Image URL copied" }))
    .catch(() => toast({ description: "Copy failed" }));
  setTimeout(() => toast({ description: null }), 1400);
}

// --- PATCH: Scryfall helpers (backend-first) ---
async function sfToJson(res: Response | null | undefined) {
  try { if (!res || !("ok" in res) || !res.ok) return null; return await res.json(); }
  catch { return null; }
}

function sfPickImage(sf: any): string | null {
  if (!sf || sf.image_status === "missing") return null;
  const direct =
    sf?.image_uris?.normal ??
    sf?.image_uris?.large ??
    sf?.image_uris?.small ??
    null;
  if (direct) return direct;
  if (Array.isArray(sf?.card_faces)) {
    for (const f of sf.card_faces) {
      const img =
        f?.image_uris?.normal ??
        f?.image_uris?.large ??
        f?.image_uris?.small ??
        null;
      if (img) return img;
    }
  }
  return null;
}

// small cache to avoid duplicate fetches
const __sfByIdCache = new Map<string, Promise<any | null>>();

async function sfFetchByCardIdBackendFirst(cardId: string): Promise<any | null> {
  if (!cardId) return null;
  if (__sfByIdCache.has(cardId)) return __sfByIdCache.get(cardId)!;

  const p = (async () => {
    try {
      // Prefer your scry client if it has a direct method:
      if (typeof (scry as any)?.getById === "function") {
        const r = await (scry as any).getById(cardId);
        const j = await sfToJson(r);
        if (j?.id) return j;
      }
      if (typeof (scry as any)?.getCard === "function") {
        const r = await (scry as any).getCard({ id: cardId });
        const j = await sfToJson(r);
        if (j?.id) return j;
      }
    } catch {}
    // Public fallback
    try {
      const r = await fetch(`https://api.scryfall.com/cards/${encodeURIComponent(cardId)}`);
      return await sfToJson(r);
    } catch { return null; }
  })();

  __sfByIdCache.set(cardId, p);
  return p;
}

  async function saveCardImage(card: ListingData) {
  try {
    const img = scryImg(card.scryfall_id, "normal");
    if (!img) return;
    const imgRes = await fetch(img, { mode: "cors" });
    const blob = await imgRes.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(card.name || "card").replace(/[^a-z0-9_-]+/gi, "_")}.jpg`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 1000);
  } catch {
    toast({ description: "Save failed" });
    setTimeout(() => toast({ description: null }), 1400);
  }
}


  function deriveDistanceMiles(
    extra: any,
    base: ListingProfile,
    meLatStr: string | null,
    meLngStr: string | null
  ): number | null {
    const meLat = parseFloat(meLatStr || "");
    const meLng = parseFloat(meLngStr || "");
    if (!Number.isFinite(meLat) || !Number.isFinite(meLng)) return null;

    // Try multiple possible field names for other user's coords
    const candLat =
      pickFirstNumber(
        extra?.lat,
        extra?.latitude,
        extra?.loc?.lat,
        extra?.location?.lat,
        (base as any)?.lat
      );
    const candLng =
      pickFirstNumber(
        extra?.lng,
        extra?.lon,
        extra?.long,
        extra?.longitude,
        extra?.loc?.lng,
        extra?.location?.lng,
        (base as any)?.lng
      );
    if (candLat == null || candLng == null) return null;

    return haversineMiles(meLat, meLng, candLat, candLng);
  }

  function pickFirstNumber(...vals: any[]): number | null {
    for (const v of vals) {
      const n = typeof v === "string" ? parseFloat(v) : v;
      if (typeof n === "number" && Number.isFinite(n)) return n;
    }
    return null;
  }

  function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371; // km
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const km = R * c;
    return km * 0.621371; // miles
  }
} // <-- close ListingProfileCard component

/* NEW FocusedProfileModal (mirrors NewMiniProfileCard variant) */
function FocusedProfileModal(props: { children: React.ReactNode; onClose: () => void; ariaLabel: string }) {
  const { children, onClose, ariaLabel } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const f = ref.current.querySelectorAll<HTMLElement>(
          'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-2 sm:p-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        className="relative w-[96vw] max-w-[1280px] h-[92vh] overflow-hidden rounded-2xl border border-white/15 bg-slate-950/85 backdrop-blur-md shadow-2xl focus:outline-none"
        tabIndex={-1}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-50 rounded-md bg-black/50 hover:bg-black/70 text-white px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-400"
        >
          Close
        </button>
        <div className="w-full h-full overflow-auto pr-1">{children}</div>
      </div>
    </div>
  );
}

/* NEW: Lightbox component (adapted from NewMiniProfileCard ImageLightbox) */
function ListingImageLightbox(props: {
  listing: ListingData;
  onClose: () => void;
  onOffer: () => void;
  onMessage: () => void;
  onShare: () => void;
}) {
  const { listing, onClose, onOffer, onMessage, onShare } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const focusables = ref.current.querySelectorAll<HTMLElement>(
          'a,button,[tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (document.activeElement === first && e.shiftKey) {
          e.preventDefault(); last.focus();
        } else if (document.activeElement === last && !e.shiftKey) {
          e.preventDefault(); first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const img = scryImg(listing.scryfall_id, "normal") || "/images/sg-card-back.png";


  return (
    <div
      className="fixed inset-0 z-[3500] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={listing.name}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={ref} className="relative flex flex-col items-center gap-4">
        <button
          onClick={onClose}
            className="absolute -top-6 right-0 md:top-0 md:-right-10 rounded-md bg-black/55 hover:bg-black/80 text-white text-xs px-2 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-400"
        >
          ✕
        </button>
        <img
          src={img}
          alt={listing.name}
          className="select-none pointer-events-none rounded-lg shadow-xl max-h-[78vh] max-w-[78vw] w-auto h-auto object-contain"
          draggable={false}
        />
        <div className="listing-image-lightbox-actions flex flex-wrap items-center justify-center gap-3 mt-1 text-xs">
          {(listing.status === "sale" || listing.status === "trade") && (
            <button
              onClick={onOffer}
              className="rounded-md px-4 py-2 font-semibold bg-amber-400 text-black hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              {listing.status === "sale" ? "Offer" : "Trade"}
            </button>
          )}
          <button
            onClick={onMessage}
            className="rounded-md px-4 py-2 font-semibold bg-white/15 text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          >
            Message
          </button>
          <button
            onClick={onShare}
            className="rounded-md px-4 py-2 font-semibold bg-white/15 text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          >
            Share
          </button>
          <a
            href={img}
            download={(listing.name || "card").replace(/\s+/g, "_") + ".jpg"}
            className="rounded-md px-4 py-2 font-semibold bg-white/15 text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          >
            Download
          </a>
        </div>
      </div>
    </div>
  );
}

